import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import useNextHotelBookingStay from 'hooks/Search/useNextHotelBookingStay'
import moment, { type Moment } from 'moment'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  formatDateRangeLabel,
  getThisWeekendRange,
} from 'lib/search/experiences'
import styled from 'styled-components'
import { rem } from 'polished'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'

const MultiLineContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  margin-top: ${rem(8)};
`

export const CHIP_ANYTIME = 'anytime'
export const CHIP_TOMORROW = 'tomorrow'
export const CHIP_WEEKEND = 'weekend'
export const CHIP_NEXT_BOOKING = 'next-booking'

export type ExperiencesDateQuickChipValue =
  | typeof CHIP_ANYTIME
  | typeof CHIP_TOMORROW
  | typeof CHIP_WEEKEND
  | typeof CHIP_NEXT_BOOKING

/** Payload when a chip is selected: value + optional date range (omit range for Anytime). */
export interface ExperiencesDateQuickChipSelectPayload {
  value: ExperiencesDateQuickChipValue
  range?: { start: Moment, end: Moment }
}

export { getThisWeekendRange }

interface Props {
  /** Called with chip value and date range when applicable (Anytime has no range). Parent applies to context/state. */
  onSelect: (payload: ExperiencesDateQuickChipSelectPayload) => void
  /** Current check-in date; when provided with checkoutDate/dateSearchOptionId, highlights the matching chip. */
  checkinDate?: Moment
  /** Current check-out date. */
  checkoutDate?: Moment
  /** Current date search option (e.g. ANYTIME). Omit when no context/state (e.g. search page). */
  dateSearchOptionId?: string
  className?: string
  displayType?: 'single-line' | 'multi-line'
  showNextBooking?: boolean
  /** When provided, chips whose date range falls entirely outside [minDate, maxDate] are hidden. */
  minDate?: Date
  maxDate?: Date
}

function ExperiencesSearchDateQuickChips(props: Props) {
  const { onSelect, checkinDate, checkoutDate, dateSearchOptionId, className, displayType = 'single-line', showNextBooking = true, minDate, maxDate } = props
  const nextHotelStay = useNextHotelBookingStay()
  const intl = useIntl()

  const { anytimePlaceholder, tomorrowLabel, thisWeekendLabel } = useExperienceSearchLabels()

  const chips = useMemo(() => {
    const isAvailable = (start: Moment, end: Moment) => {
      if (maxDate && start.toDate() > maxDate) return false
      if (minDate && end.toDate() < minDate) return false
      return true
    }

    const tomorrow = moment().add(1, 'day').startOf('day')
    const weekend = getThisWeekendRange()

    const base: Array<{
      value: ExperiencesDateQuickChipValue
      label: string
      startIcon: React.ReactElement | undefined
    }> = [
      {
        value: CHIP_ANYTIME,
        label: anytimePlaceholder,
        startIcon: undefined,
      },
    ]
    if (isAvailable(tomorrow, tomorrow)) {
      base.push({ value: CHIP_TOMORROW, label: tomorrowLabel, startIcon: undefined })
    }
    if (isAvailable(weekend.start, weekend.end)) {
      base.push({ value: CHIP_WEEKEND, label: thisWeekendLabel, startIcon: undefined })
    }
    if (showNextBooking && nextHotelStay && isAvailable(nextHotelStay.checkIn, nextHotelStay.checkOut)) {
      base.push({
        value: CHIP_NEXT_BOOKING,
        label: formatDateRangeLabel(nextHotelStay.checkIn, nextHotelStay.checkOut, intl.locale),
        startIcon: <LineBedIcon size="S" />,
      })
    }
    return base
  }, [nextHotelStay, anytimePlaceholder, tomorrowLabel, thisWeekendLabel, minDate, maxDate, intl.locale, showNextBooking])

  const selectedChip = useMemo(() => {
    const hasSelection = checkinDate !== undefined || checkoutDate !== undefined || dateSearchOptionId !== undefined
    if (!hasSelection) {
      return undefined
    }
    if (!checkinDate && !checkoutDate && (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME || !dateSearchOptionId)) {
      return CHIP_ANYTIME
    }
    if (checkinDate && checkoutDate) {
      if (checkinDate.isSame(moment().add(1, 'day'), 'day') && checkoutDate.isSame(moment().add(1, 'day'), 'day')) {
        return CHIP_TOMORROW
      }
      const { start, end } = getThisWeekendRange()
      if (checkinDate.isSame(start, 'day') && checkoutDate.isSame(end, 'day')) {
        return CHIP_WEEKEND
      }
      if (
        nextHotelStay &&
        checkinDate.isSame(nextHotelStay.checkIn, 'day') &&
        checkoutDate.isSame(nextHotelStay.checkOut, 'day')
      ) {
        return CHIP_NEXT_BOOKING
      }
    }
    return undefined
  }, [checkinDate, checkoutDate, dateSearchOptionId, nextHotelStay])

  const handleChipClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const value = e.currentTarget.dataset.value as ExperiencesDateQuickChipValue | undefined
    if (value === CHIP_ANYTIME || value === selectedChip) {
      onSelect({ value: CHIP_ANYTIME })
      return
    }
    if (value === CHIP_TOMORROW) {
      const d = moment().add(1, 'day').startOf('day')
      onSelect({ value: CHIP_TOMORROW, range: { start: d, end: d } })
      return
    }
    if (value === CHIP_WEEKEND) {
      onSelect({ value: CHIP_WEEKEND, range: getThisWeekendRange() })
      return
    }
    if (value === CHIP_NEXT_BOOKING && nextHotelStay) {
      onSelect({
        value: CHIP_NEXT_BOOKING,
        range: { start: nextHotelStay.checkIn, end: nextHotelStay.checkOut },
      })
    }
  }, [nextHotelStay, onSelect, selectedChip])

  const Container = displayType === 'single-line' ? HorizontalContainerWithFadedOverflow : MultiLineContainer

  return (
    <Container className={className} overflowGutter={16} gap={8}>
      {chips.map(chip => (
        <FilterChip
          key={chip.value}
          size="medium"
          selected={selectedChip === chip.value}
          data-value={chip.value}
          onClick={handleChipClick}
          startIcon={chip.startIcon}
        >
          {chip.label}
        </FilterChip>
      ))}
    </Container>
  )
}

export default ExperiencesSearchDateQuickChips
